import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SoldPlate, SaleType } from 'src/app/models/soldPlate';
import { Registration } from 'src/app/models/registration';
import { PlatePaymentDetails } from 'src/app/models/platePaymentDetails';
import {
  ExtendedSearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { DatelessSearchResponse } from '../../../dateless-search-page/dateless-search-page.component';
import {
  IndexPoint,
  IndexSeriesFile,
  MarketFormatKey,
  routeToSeries,
  indexValueAt,
  SERIES_LABELS,
  CohortSalesFile,
  CohortDef,
  CohortSalePoint,
  routeToCohort,
  DatelessLayoutFile,
  DatelessEarlyFile,
  DatelessParts,
  parseDatelessParts,
  routeToDatelessLayout,
  datelessMatch,
  sameNumberClass,
  describeDatelessCohort,
  datelessLayoutName,
  yearlyMedians,
  WHOLE_LAYOUT_MAX,
  CROWDED_NUMBER,
  THIN_COHORT,
  TINY_LAYOUT,
} from './market-index.model';

// One rendered sale marker pinned onto the index line at its sale year.
interface SaleMarker {
  cx: number;
  cy: number;
  labelX: number;
  anchor: 'start' | 'middle' | 'end';
  price: string;
  year: number;
  isSelf: boolean;
}

interface ChartVM {
  seriesName: string;
  title: string;
  sub: string;
  tag: string;
  blurb: string;
  metaRange: string; // e.g. "1990 to 2026 · 2015 = 100"
  viewBox: string;
  plot: { x: number; y: number; w: number; h: number };
  gridlines: { y: number; label: string }[];
  xTicks: { x: number; label: string }[];
  ciBandPath: string | null;
  solidPath: string;
  dashedPath: string | null;
  nodes: { cx: number; cy: number; provisional: boolean }[];
  refLine: { x: number; label: string } | null;
  /** Years above the capped axis: drawn at the top edge with an arrow and the real value. */
  offScale: { x: number; label: string; title: string }[];
  markers: SaleMarker[];
  note: string;
  source: string;
  /** Computed headline figures, e.g. "▲ 6.5%" (so far in 2026) and "▲ 80.1%" (against 2015). */
  figures: KeyFigure[];
}

// One headline figure in a card's key-figure strip (above each chart).
interface KeyFigure {
  k: string;
  v: string;
  dir?: 'up' | 'down' | 'flat';
}

// One verified-clearance dot in the cohort scatter (Chart B).
interface CohortDot {
  cx: number;
  cy: number;
  isSister: boolean; // L_NN sister format (grey) vs primary NN_L (green)
  title: string; // "30O — £79,688 — 2015-02" (native a11y fallback)
  // Structured fields for the interactive tap/hover tooltip.
  plate: string;
  priceLabel: string; // "£79,688"
  ym: string; // "2015-02"
  clsLabel: string; // primary/sister structural-class label
}

// Interactive tooltip box for a Chart B dot (tap on mobile, hover on desktop).
interface CohortTip {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: { t: string; head?: boolean }[];
}

// One of this plate's own verified clearances, drawn gold on Chart B. When the
// plate has several, they are connected by a dashed gold line and only the
// latest carries the visible label + axis date (to avoid clutter).
interface OwnMarker {
  cx: number;
  cy: number;
  tickY: number;
  label: string;
  when: string;
  isLatest: boolean;
  anchor: 'start' | 'middle' | 'end'; // keeps the label inside the plot at the edges
  title: string; // native a11y tooltip
}

// The cohort verified-sales scatter view-model (prototype Chart B).
interface CohortVM {
  title: string;
  metaRange: string; // "129 auction clearances · 2015–2026"
  blurb: string;
  viewBox: string;
  plot: { x: number; y: number; w: number; h: number };
  gridlines: { y: number; label: string }[];
  xTicks: { x: number; label: string }[];
  refLine: { x: number } | null;
  medianPath: string;
  medianNodes: { cx: number; cy: number }[];
  medianLabels: { x: number; y: number; text: string }[];
  dots: CohortDot[];
  // All of this plate's own verified clearances (gold). Empty when none.
  owns: OwnMarker[];
  // Dashed gold connector through the own markers (only when ≥2 own sales).
  ownConnectorPath: string | null;
  primaryLabel: string;
  sisterLabel: string;
  hasSister: boolean;
  note: string;
  source: string;
}

// One comparable registration plotted on the Comparable Market strip. Each is a
// real, live-advertised registration from the same cohort, positioned by its
// ACTUAL asking price so the user reads relative position visually. Carries the
// raw registration so it can deep-link and lazily resolve its dealer count.
interface CompPoint {
  cx: number;
  cy: number;
  regRaw: string; // canonical reg for navigation + dealer-count lookup
  regFmt: string; // display reg
  priceLabel: string;
}

// Interactive tooltip for a Chart 1 comparable dot (registration / asking price
// / dealer count / tap-to-view). Mirrors the Chart B CohortTip box.
interface CompTip {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: { t: string; head?: boolean }[];
}

// "Comparable Market" card (Chart 1) — the market-POSITION visual answering
// "Where does this registration sit within today's comparable market?". The
// point cloud is ALWAYS live advertised comparable registrations plotted at
// their ACTUAL asking prices (never verified sales — that is Chart 2). When the
// subject is itself advertised it is highlighted at its real asking price
// (`subject`); when it is not advertised a SEPARATE cohort market-signal marker
// (`signal`) is shown instead, clearly labelled as a signal, not a valuation.
// IQR/median context is derived from the live cloud and demoted beneath.
interface ComparableMarketVM {
  heading: string;
  subtitle: string;
  tag: string;
  sourceLabel: string;
  countLabel: string;
  hasChart: boolean; // false → only the empty note + supporting line render
  viewBox: string;
  plot: { x: number; y: number; w: number; h: number };
  xTicks: { x: number; label: string }[];
  iqrBand: { x: number; w: number } | null; // from the LIVE cloud
  medianX: number | null; // LIVE-cloud median
  points: CompPoint[]; // close cohort (shares the plate's letter or number) — green
  // Wider same-format NN_L universe (any letter), grey dots behind the green
  // close cohort. Empty when not applicable (non-dateless / none available).
  widePoints: CompPoint[];
  wideLabel: string | null; // legend label for the grey series (null = hide)
  // The subject at its ACTUAL asking price (only when it is itself advertised).
  subject: {
    cx: number;
    cy: number;
    axisY: number;
    label: string;
    sub: string;
  } | null;
  // A separate cohort market-signal marker (only when NOT advertised). Distinct
  // dashed/hollow gold so it never reads as a real listing or a valuation.
  signal: {
    cx: number;
    cy: number;
    axisY: number;
    label: string;
    sub: string;
  } | null;
  emptyNote: string | null;
  // Supporting live-market stats (rendered beneath the chart, de-emphasised).
  liveLabel: string;
  liveLo: string | null;
  liveMedian: string | null;
  liveHi: string | null;
  liveBasis: string;
  framing: string;
  disclaimer: string;
  source: string;
}

// "Previous Sale in Context" card — condensed to four facts: last verified sale
// price, sale date, cohort median at the time, relative position vs cohort.
interface PrevSaleVM {
  price: string;
  date: string;
  cohortMedian: string | null;
  deltaLabel: string;
  deltaDir: 'below' | 'above' | 'inline' | 'na';
}

/**
 * Plate Detail v2 — Market Information section.
 *
 * Renders a precomputed PlateX index series (routed by plate format) in the
 * prototype's editorial graph style, then overlays the plate's own verified
 * sale markers. The index line is shared across every plate in the same
 * segment; only the markers differ per registration. Falls back to a quiet
 * panel when no confident series applies. Touches no other v2 module.
 */
@Component({
  selector: 'pla-pdv2-market-info',
  templateUrl: './market-info.component.html',
  styleUrls: ['./market-info.component.scss'],
})
export class MarketInfoComponent
  implements OnInit, OnChanges, AfterViewChecked
{
  // Display registration, e.g. "72 K" — used only in copy, never to build data.
  @Input() registration = '';
  // Compact reg, e.g. "72K" — used for dateless structural sub-routing.
  @Input() compactRegistration = '';
  // The format key the v2 page already derives (never guessed here).
  @Input() formatKey: MarketFormatKey = null;
  // The plate's own verified sale/auction records (same source as the timeline:
  // registrationService.getPlateHistory). Used only to position markers.
  @Input() sales: SoldPlate[] | null = null;
  // The subject plate's own current asking price in £ (parent `fromPrice`,
  // derived from platexPrice). Non-null only when the plate carries a direct
  // PlateX price; used to plot the subject at its real listed price.
  @Input() askingPrice: number | null = null;
  // The lowest live dealer-listing total in £ (parent `marketRange.low`). This
  // is the registration's advertised price when it is on the market through
  // dealers but has no direct PlateX price (platexPrice). Used as the
  // advertised-price fallback so a plate on the market is plotted at its real
  // price rather than shown as a signal.
  @Input() marketLow: number | null = null;
  // True when the plate is DVLA fixed-price stock (the page's "New Issue"
  // status). Such plates get neither evidence card: they have a fixed price,
  // not a market position.
  @Input() newIssue = false;

  // Loaded once; cached across plate navigations within the session.
  private static cache: IndexSeriesFile | null = null;
  private file: IndexSeriesFile | null = MarketInfoComponent.cache;
  private loadError = false;

  // Cohort verified-sales dataset (Chart B); cached the same way.
  private static cohortCache: CohortSalesFile | null = null;
  private cohortFile: CohortSalesFile | null = MarketInfoComponent.cohortCache;
  private cohortLoadError = false;

  // Per-layout dateless sales files (every dateless layout other than the
  // 3-character single-letter one), fetched on demand and cached by key.
  private static layoutCache: Map<string, DatelessLayoutFile> = new Map();
  private static layoutFailed: Set<string> = new Set();
  // Pre-2016 sales per layout, fetched only for thin cohorts.
  private static earlyCache: Map<string, DatelessEarlyFile> = new Map();
  private static earlyPending: Set<string> = new Set();
  private layoutReqKey = '';
  // The comparable cohort built from a layout file, memoised per plate.
  private layoutCohortFor = '';
  private layoutCohort: CohortDef | null = null;

  // Asking-price cohort: true when the bare number matched so many listings
  // that only same-number plates with the same first letter were kept.
  private askCrowded = false;

  chart: ChartVM | null = null;
  cohort: CohortVM | null = null;
  comparable: ComparableMarketVM | null = null;
  prevSale: PrevSaleVM | null = null;

  // Live comparable listings (priced registrations from the same cohort),
  // fetched once per registration via the EXISTING search primitives and cached
  // for the session. null = not yet loaded; [] = loaded, none available.
  private compListings:
    | { regRaw: string; regFmt: string; pricePounds: number }[]
    | null = null;
  private static compCache: Map<
    string,
    { regRaw: string; regFmt: string; pricePounds: number }[]
  > = new Map();
  private compReqKey = '';

  // Wider same-format evidence (dateless only): ALL priced NN_L registrations of
  // this exact layout, any letter — fetched via a single empty-term whole-layout
  // search and plotted as quiet grey dots BEHIND the close-cohort green dots, to
  // add market density. Cached per layout (shared by every reg of that layout).
  // null = not yet loaded; [] = loaded/none or not applicable (non-dateless).
  private wideListings:
    | { regRaw: string; regFmt: string; pricePounds: number }[]
    | null = null;
  private static wideCache: Map<
    string,
    { regRaw: string; regFmt: string; pricePounds: number }[]
  > = new Map();

  // Active Chart B tooltip (set on dot tap/hover; null when dismissed).
  activeTip: CohortTip | null = null;

  // Active Chart 1 comparable tooltip + the reg it belongs to (so a late dealer
  // count only patches the tip if the user is still on that dot).
  activeCompTip: CompTip | null = null;
  private activeCompReg = '';
  // Dealer counts resolved lazily per reg on hover, cached for the session.
  private static dealerCounts: Map<string, number> = new Map();

  // Chart 1 horizontal-scroll wrapper. On narrow screens the strip overflows
  // and scrolls; we centre it on the plate's own marker once the chart (incl.
  // the wider grey series, which sets the final x-domain) has rendered.
  @ViewChild('chartScroll') private chartScrollRef?: ElementRef<HTMLElement>;
  private centerPending = false;
  private centeredFor = '';

  // Plot geometry (matches the prototype's .scatter-svg proportions).
  private readonly W = 780;
  private readonly H = 248;
  private readonly PAD_L = 58;
  private readonly PAD_R = 748;
  private readonly PAD_T = 18;
  private readonly PAD_B = 206;

  // Cohort scatter geometry (taller — matches the prototype's Chart B 780×290).
  private readonly CH = 290;
  private readonly CPAD_T = 24;
  private readonly CPAD_B = 250;

  // Comparable Market strip geometry (horizontal price positioning band).
  private readonly CMH = 218;
  private readonly CM_T = 46; // band top
  private readonly CM_B = 150; // band bottom
  private readonly CM_AXIS_Y = 172;

  constructor(
    private http: HttpClient,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAssets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['registration'] ||
      changes['compactRegistration'] ||
      changes['formatKey']
    ) {
      this.loadComparables();
      this.loadLayout();
    }
    this.build();
  }

  // Load both precomputed static assets (each bypasses the [HTTP-*] placeholder
  // interceptor — no prefix). build() is idempotent and re-runs as each
  // resolves, so the two loads are independent.
  private loadAssets(): void {
    if (this.file || this.loadError) {
      this.build();
    } else {
      this.http
        .get<IndexSeriesFile>('assets/market-index/platex-index-series.json')
        .subscribe(
          (data) => {
            MarketInfoComponent.cache = data;
            this.file = data;
            this.build();
          },
          () => {
            this.loadError = true;
            this.build();
          }
        );
    }

    if (this.cohortFile || this.cohortLoadError) {
      this.build();
    } else {
      this.http
        .get<CohortSalesFile>('assets/market-index/platex-cohort-sales.json')
        .subscribe(
          (data) => {
            MarketInfoComponent.cohortCache = data;
            this.cohortFile = data;
            this.build();
          },
          () => {
            this.cohortLoadError = true;
            this.build();
          }
        );
    }
  }

  // Fetch this plate's dateless layout sales file (other layouts than the
  // live 3-character single-letter cohort). build() re-runs when it lands.
  private loadLayout(): void {
    const key = routeToDatelessLayout(this.formatKey, this.compactRegistration);
    if (!key || key === this.layoutReqKey) return;
    this.layoutReqKey = key;
    if (
      MarketInfoComponent.layoutCache.has(key) ||
      MarketInfoComponent.layoutFailed.has(key)
    ) {
      this.build();
      return;
    }
    this.http
      .get<DatelessLayoutFile>(`assets/market-index/cohorts/${key}.json`)
      .subscribe(
        (data) => {
          MarketInfoComponent.layoutCache.set(key, data);
          if (this.layoutReqKey === key) this.build();
        },
        () => {
          MarketInfoComponent.layoutFailed.add(key);
        }
      );
  }

  // Fetch a layout's pre-2016 sales; the cohort is rebuilt when they land.
  private loadEarly(key: string): void {
    if (MarketInfoComponent.earlyPending.has(key)) return;
    MarketInfoComponent.earlyPending.add(key);
    this.http
      .get<DatelessEarlyFile>(`assets/market-index/cohorts/${key}-early.json`)
      .subscribe(
        (data) => {
          MarketInfoComponent.earlyCache.set(key, data);
          this.layoutCohortFor = '';
          if (this.layoutReqKey === key) this.build();
        },
        () => {
          // No early file (nothing sold before 2016): stay with 2016 onwards.
        }
      );
  }

  private build(): void {
    this.activeTip = null;
    this.activeCompTip = null;
    this.activeCompReg = '';
    this.buildIndex();
    this.buildComparable();
    this.buildPrevSale();
    this.buildCohort();

    // Request a one-time horizontal centre for this plate, but only once the
    // wider grey series has resolved (wideListings !== null) — it sets the final
    // x-domain, so centring earlier would land on a stale subject position.
    if (
      this.comparable?.hasChart &&
      this.wideListings !== null &&
      this.centeredFor !== this.compactRegistration
    ) {
      this.centerPending = true;
    }
  }

  // After the chart DOM is in place, scroll the strip so the plate's own marker
  // (or the market middle when it is not for sale) sits in the centre of the
  // viewport. No-op on wide screens where the strip already fits.
  ngAfterViewChecked(): void {
    if (!this.centerPending) return;
    const el = this.chartScrollRef?.nativeElement;
    const svg = el?.querySelector('svg.scatter-svg') as SVGSVGElement | null;
    if (!el || !svg || !this.comparable?.hasChart) return;
    if (!svg.clientWidth) return; // not laid out yet — try again next cycle
    this.centerPending = false;
    this.centeredFor = this.compactRegistration;

    const cm = this.comparable;
    const focusVbX =
      cm.subject?.cx ??
      cm.signal?.cx ??
      (cm.iqrBand ? cm.iqrBand.x + cm.iqrBand.w / 2 : null) ??
      cm.medianX ??
      null;
    if (focusVbX == null) return;
    // viewBox 0..W maps onto the rendered SVG width; convert + centre, clamped.
    const pxX = (focusVbX / this.W) * svg.clientWidth;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollLeft = Math.max(0, Math.min(pxX - el.clientWidth / 2, max));
  }

  // Fetch live comparable listings for this registration via the EXISTING
  // search primitives (the same data path the "Similar registrations" section
  // uses — no new backend). Collects every available, priced comparable as a
  // candidate scatter point. Cached per compact registration for the session.
  private loadComparables(): void {
    const key = (this.compactRegistration || '').toUpperCase();
    if (!key) {
      this.compListings = [];
      return;
    }
    if (this.compReqKey === key) return; // already loading/loaded this plate
    this.compReqKey = key;

    const cached = MarketInfoComponent.compCache.get(key);
    if (cached) {
      this.compListings = cached;
      this.build();
      return;
    }
    this.compListings = null;

    const finish = (raw: Registration[]) => {
      const out = this.toComparableListings(key, raw);
      MarketInfoComponent.compCache.set(key, out);
      // Ignore a stale response if the plate changed mid-flight.
      if (this.compReqKey !== key) return;
      this.compListings = out;
      this.build();
    };

    // Dateless plates: reuse the arrangement-cohort canonical dateless search
    // (shares the plate's letter or number group). Others: extended-search.
    const dl = this.formatKey !== 'ni' ? this.parseDateless(key) : null;
    if (dl) {
      const mask =
        '*'.repeat(dl.letters.length) + 'x' + '-'.repeat(dl.numbers.length);
      const subject = parseDatelessParts(key);
      // Wider same-layout universe (any letters, either arrangement), grey
      // dots — dateless only.
      this.loadWide(key, mask);
      this.searchService.searchDatelessLengthCanonical(
        mask,
        dl.letters,
        (letterRes: DatelessSearchResponse) => {
          this.searchService.searchDatelessLengthCanonical(
            mask,
            dl.numbers,
            (numberRes: DatelessSearchResponse) => {
              // A bare number shared by a crowd of listings (ABC 1 against
              // every 3-letter number 1) is not a comparable on its own: keep
              // only those that also share the first letter.
              let byNumber = ((numberRes && numberRes.main) || []).filter(
                (r) => r && r.price > 0 && !this.isDvlaStock(r)
              );
              this.askCrowded = byNumber.length > 60;
              if (this.askCrowded && subject) {
                byNumber = byNumber.filter((r) => {
                  const o = parseDatelessParts(
                    (r.registration || '').replace(/\s+/g, '')
                  );
                  return !!o && o.letters[0] === subject.letters[0];
                });
              }
              finish([...((letterRes && letterRes.main) || []), ...byNumber]);
            }
          );
        }
      );
      return;
    }

    // Non-dateless plates have no NN_L wider-format series.
    this.wideListings = [];

    const style =
      this.formatKey === 'prefix' || this.formatKey === 'suffix'
        ? this.formatKey
        : 'current';
    this.searchService.extendedSearch(
      key,
      style,
      (groups: ExtendedSearchReqResponse[]) => {
        const flat = (groups || []).reduce<Registration[]>(
          (acc, g) => acc.concat(g && g.results ? g.results : []),
          []
        );
        finish(flat);
      }
    );
  }

  // Format + filter raw search results into priced comparable listings: exclude
  // self, available/priced only, dedupe. Prices are pence on Registration →
  // pounds here. Never invents a price.
  private toComparableListings(
    key: string,
    raw: Registration[]
  ): { regRaw: string; regFmt: string; pricePounds: number }[] {
    const formatted = this.registrationService.formatRegistrations(
      (raw || []).slice()
    );
    const seen = new Set<string>([key]);
    const out: { regRaw: string; regFmt: string; pricePounds: number }[] = [];
    for (const r of formatted) {
      if (!r || !r.registration) continue;
      const rc = r.registration.replace(/\s+/g, '').toUpperCase();
      if (seen.has(rc)) continue;
      if (!(r.price > 0)) continue; // priced/available only
      if (this.isDvlaStock(r)) continue; // DVLA fixed price, not a dealer ask
      if (/^\d+O$/.test(rc)) continue; // exclude NN_O — the "O" reads as a zero
      seen.add(rc);
      out.push({
        regRaw: r.registration,
        regFmt: r.formattedRegistration || r.registration,
        pricePounds: Math.round(r.price / 100),
      });
    }
    return out;
  }

  // DVLA-owned stock (fixed-price or DVLA auction) is not a dealer asking
  // price. Search results carry the owner as a number or a string.
  private isDvlaStock(r: Registration): boolean {
    const o = Number(
      (r as any).plate_owner != null ? (r as any).plate_owner : r.plateOwner
    );
    return o === 0 || o === 4 || o === 5 || o === 6;
  }

  // Fetch the wider same-format NN_L universe for a dateless layout (any letter)
  // via a single empty-term whole-layout canonical search. Cached per layout so
  // every reg of the same layout reuses it. Filtered to priced NN_L only.
  private loadWide(key: string, mask: string): void {
    const ck = 'W:' + mask;
    const cached = MarketInfoComponent.wideCache.get(ck);
    if (cached) {
      this.wideListings = cached;
      return;
    }
    this.wideListings = null;
    this.searchService.searchDatelessLengthCanonical(
      mask,
      '',
      (res: DatelessSearchResponse) => {
        const out = this.toWideLayout(key, (res && res.main) || []);
        MarketInfoComponent.wideCache.set(ck, out);
        // Ignore a stale response if the plate changed mid-flight.
        if (this.compReqKey !== key) return;
        this.wideListings = out;
        this.build();
      }
    );
  }

  // Format + filter the whole-layout results into priced listings of the same
  // layout (any letters, either arrangement): exclude self, priced only,
  // dealer stock only, dedupe. The close-cohort overlap is removed later in
  // buildComparable so a reg is never plotted both green and grey.
  private toWideLayout(
    key: string,
    raw: Registration[]
  ): { regRaw: string; regFmt: string; pricePounds: number }[] {
    const formatted = this.registrationService.formatRegistrations(
      (raw || []).slice()
    );
    const seen = new Set<string>([key]);
    const out: { regRaw: string; regFmt: string; pricePounds: number }[] = [];
    for (const r of formatted) {
      if (!r || !r.registration) continue;
      const rc = r.registration.replace(/\s+/g, '').toUpperCase();
      if (seen.has(rc)) continue;
      if (!(r.price > 0)) continue; // priced/available only
      if (this.isDvlaStock(r)) continue; // DVLA fixed price, not a dealer ask
      if (!/^(\d+[A-Z]+|[A-Z]+\d+)$/.test(rc)) continue; // dateless shape only
      if (/^\d+O$/.test(rc)) continue; // exclude NN_O — the "O" reads as a zero
      seen.add(rc);
      out.push({
        regRaw: r.registration,
        regFmt: r.formattedRegistration || r.registration,
        pricePounds: Math.round(r.price / 100),
      });
    }
    return out;
  }

  private parseDateless(
    compact: string
  ): { letters: string; numbers: string } | null {
    let m = /^(\d+)([A-Z]+)$/.exec(compact);
    if (m) return { numbers: m[1], letters: m[2] };
    m = /^([A-Z]+)(\d+)$/.exec(compact);
    if (m) return { letters: m[1], numbers: m[2] };
    return null;
  }

  /** True when we have an index chart to show; otherwise the template shows fallback. */
  get hasChart(): boolean {
    return this.chart != null;
  }

  get unavailable(): boolean {
    return this.chart == null && this.cohort == null;
  }

  /** True when this plate's reference cohort has a verified-sales scatter. */
  get hasCohort(): boolean {
    return this.cohort != null;
  }

  private buildIndex(): void {
    this.chart = null;
    if (!this.file || this.loadError) return;

    const seriesName = routeToSeries(this.formatKey, this.compactRegistration);
    if (!seriesName) return;
    const series = this.file.series[seriesName];
    if (!series || !series.points || series.points.length < 2) return;

    // Display window: the whole series (dateless, prefix and suffix run from
    // 1990, NI from 2014, current from 2004). The index is based 2015 = 100 but is not cut
    // there: the years before the base are shown too.
    const endYear = series.year_max;
    const startYear = series.year_min;
    const pts = series.points.filter(
      (p) => p.year >= startYear && p.year <= endYear
    );
    if (pts.length < 2) return;

    // ---- y domain from values + CI bounds, padded, with nice ticks ----
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of pts) {
      lo = Math.min(lo, p.value, p.ci_lo ?? p.value);
      hi = Math.max(hi, p.value, p.ci_hi ?? p.value);
    }
    // Cap the axis at 1.6x the highest level of the last 15 years, so a few
    // early peaks (suffix 1990, current style 2004-07) don't squash the recent
    // line. Years above the cap run off the top with their value shown.
    let recentHi = -Infinity;
    for (const p of pts) {
      if (p.year >= endYear - 15) recentHi = Math.max(recentHi, p.value, p.ci_hi ?? p.value);
    }
    if (recentHi > 0 && hi > recentHi * 1.6) hi = recentHi * 1.6;
    const padY = (hi - lo) * 0.12 || 10;
    lo = Math.max(0, lo - padY);
    hi = hi + padY;
    const ticks = this.niceTicks(lo, hi, 4);
    lo = ticks[0];
    hi = ticks[ticks.length - 1];

    const xOf = (year: number) =>
      this.PAD_L +
      ((year - startYear) / (endYear - startYear || 1)) *
        (this.PAD_R - this.PAD_L);
    const yOf = (v: number) =>
      this.PAD_B - ((Math.min(v, hi) - lo) / (hi - lo || 1)) * (this.PAD_B - this.PAD_T);
    const offScale = pts
      .filter((p) => p.value > hi)
      .map((p) => ({
        x: +xOf(p.year).toFixed(1),
        label: Math.round(p.value).toString(),
        title: `${p.year}: ${Math.round(p.value)}, above the top of the chart`,
      }));

    // ---- gridlines + y labels ----
    const gridlines = ticks.map((t) => ({
      y: +yOf(t).toFixed(1),
      label: Math.round(t).toString(),
    }));

    // ---- x ticks: every year up to 12 years, else every 5th year on round
    //      years (1990, 1995, ...) so a 1990–2026 axis never crowds ----
    const span = endYear - startYear;
    const step = span > 12 ? 5 : 1;
    const first = step === 1 ? startYear : Math.ceil(startYear / step) * step;
    const xTicks: { x: number; label: string }[] = [];
    for (let y = first; y <= endYear; y += step) {
      xTicks.push({ x: +xOf(y).toFixed(1), label: y.toString() });
    }

    // ---- CI band polygon (only points that carry bounds) ----
    const band = pts.filter((p) => p.ci_lo != null && p.ci_hi != null);
    let ciBandPath: string | null = null;
    if (band.length >= 2) {
      const up = band
        .map((p) => `${xOf(p.year).toFixed(1)},${yOf(p.ci_hi as number).toFixed(1)}`)
        .join(' L ');
      const down = band
        .slice()
        .reverse()
        .map((p) => `${xOf(p.year).toFixed(1)},${yOf(p.ci_lo as number).toFixed(1)}`)
        .join(' L ');
      ciBandPath = `M ${up} L ${down} Z`;
    }

    // ---- index line: solid through the last 'dense' point, dashed beyond
    //      (provisional / thin tail, e.g. the current partial year) ----
    // The year the data was generated in is year to date (index v2 releases
    // carry a partial current year), so it is always drawn as provisional.
    const dataYear = new Date(this.file.meta.generated_at).getFullYear();
    let splitIdx = pts.length - 1;
    for (let i = pts.length - 1; i >= 0; i--) {
      if (pts[i].density === 'dense' && pts[i].year < dataYear) {
        splitIdx = i;
        break;
      }
    }
    const solidPts = pts.slice(0, splitIdx + 1);
    const dashedPts = pts.slice(splitIdx); // includes the join point
    const lineOf = (arr: IndexPoint[]) =>
      arr
        .map(
          (p, i) =>
            `${i === 0 ? 'M' : 'L'} ${xOf(p.year).toFixed(1)},${yOf(
              p.value
            ).toFixed(1)}`
        )
        .join(' ');
    const solidPath = lineOf(solidPts);
    const dashedPath = dashedPts.length >= 2 ? lineOf(dashedPts) : null;

    const nodes = pts
      .map((p, i) => ({
        cx: +xOf(p.year).toFixed(1),
        cy: +yOf(p.value).toFixed(1),
        provisional: i > splitIdx,
        off: p.value > hi, // off-scale years get the top-edge arrow instead
      }))
      .filter((n) => !n.off);

    // Base year from the series file (2015 = 100). When the chart reaches back
    // past it, a LABELLED "2015 = 100" line marks the base (the old unlabelled
    // 2021 marker read as an event, so it was removed in the review batch).
    const baseYear = Number(this.file.meta.base_year);
    const refLine =
      baseYear > startYear && baseYear <= endYear
        ? { x: +xOf(baseYear).toFixed(1), label: `${baseYear} = 100` }
        : null;

    // No own-sale markers on the MODELLED index line. A modelled index value is
    // not a transaction price, so pinning a real sale onto it misleads. This
    // plate's verified sale(s) are shown only on Chart B (Verified Sale History)
    // in actual-price space.
    const markers: SaleMarker[] = [];

    const labels = SERIES_LABELS[seriesName] || {
      title: 'PlateX market index',
      sub: 'How auction prices for this type of plate have moved, like for like.',
      tag: 'Modelled index',
      blurb:
        'A mix-adjusted index of plates sold at auction, like for like. The shaded band is the 95% range.',
      short: 'This segment',
    };
    const tag = labels.tag;
    const figures = this.indexFigures(series.points, dataYear, baseYear);

    this.chart = {
      seriesName,
      title: labels.title,
      sub: labels.sub,
      tag,
      blurb: labels.blurb,
      metaRange: `${startYear} to ${endYear} · ${this.file.meta.base_year} = 100`,
      viewBox: `0 0 ${this.W} ${this.H}`,
      plot: {
        x: this.PAD_L,
        y: this.PAD_T,
        w: this.PAD_R - this.PAD_L,
        h: this.PAD_B - this.PAD_T,
      },
      gridlines,
      xTicks,
      ciBandPath,
      solidPath,
      dashedPath,
      nodes,
      refLine,
      offScale,
      markers,
      note: this.indexNote(),
      source: 'Source: PlateX index of auction sales' + (this.indexDataTo() ? `, data to ${this.indexDataTo()}.` : '.'),
      figures,
    };
  }

  /** Visitor-facing note (the asset's meta.note is written for developers). */
  private indexDataTo(): string {
    const m = /Sold data to (\d{4})-(\d{2})-(\d{2})/.exec((this.file && this.file.meta.note) || '');
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December'];
    return m ? `${+m[3]} ${months[+m[2] - 1]} ${m[1]}` : '';
  }

  private indexNote(): string {
    const dataTo = this.indexDataTo();
    return (
      'PlateX Plate Price Index: plates sold at DVLA auctions and auction houses, like for like.' +
      (dataTo ? ` Sold data to ${dataTo}.` : '') +
      ' Not a valuation of this registration.'
    );
  }

  /**
   * Headline figures for the index card: the latest point against the year
   * before, and against the base year. Computed, never hand-written per plate.
   */
  private indexFigures(points: IndexPoint[], dataYear: number, baseYear: number): KeyFigure[] {
    const byYear = new Map(points.map((p) => [p.year, p.value]));
    const last = points[points.length - 1];
    const prev = byYear.get(last.year - 1);
    const base = byYear.get(baseYear);
    if (prev == null || base == null) return [];
    const fig = (pct: number, k: string): KeyFigure => {
      const dir = Math.abs(pct) < 0.5 ? 'flat' : pct > 0 ? 'up' : 'down';
      const arrow = dir === 'flat' ? '' : dir === 'up' ? '▲ ' : '▼ ';
      return { k, v: `${arrow}${Math.abs(pct).toFixed(1)}%`, dir };
    };
    const span = last.year >= dataYear ? `${last.year} so far, against ${last.year - 1}` : `${last.year}, against ${last.year - 1}`;
    const vsBase = (last.value / base - 1) * 100;
    return [
      fig((last.value / prev - 1) * 100, span),
      fig(vsBase, `${vsBase < 0 ? 'Below' : 'Above'} ${baseYear}`),
    ];
  }

  private capFirst(t: string): string {
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
  }

  /** The visible one-line source for Chart B: the "Sources: …" sentence of the data note. */
  private cohortSource(note: string): string {
    const m = /Sources?:[^.]*\./.exec(note || '');
    return m ? m[0] : 'Source: verified auction results.';
  }

  private buildMarkers(
    pts: IndexPoint[],
    startYear: number,
    endYear: number,
    xOf: (y: number) => number,
    yOf: (v: number) => number
  ): SaleMarker[] {
    if (!this.sales || this.sales.length === 0) return [];
    const compact = (this.compactRegistration || '').toUpperCase();
    const out: SaleMarker[] = [];
    for (const s of this.sales) {
      // Only auction-settled transactions are real sales — exclude listing /
      // price-change markers (mirrors the timeline's isTimelineSale).
      if (!this.isAuctionSale(s.saleType)) continue;
      const d = s && s.sold_date ? new Date(s.sold_date) : null;
      if (!d || isNaN(d.getTime())) continue;
      const fracYear = d.getFullYear() + d.getMonth() / 12;
      if (fracYear < startYear || fracYear > endYear) continue;
      const v = indexValueAt(pts, fracYear);
      if (v == null) continue;
      const price = this.formatPrice(s);
      if (!price) continue;
      const cx = +xOf(fracYear).toFixed(1);
      const cy = +yOf(v).toFixed(1);
      const anchor: 'start' | 'middle' | 'end' =
        cx < this.PAD_L + 60 ? 'start' : cx > this.PAD_R - 60 ? 'end' : 'middle';
      out.push({
        cx,
        cy,
        labelX: cx,
        anchor,
        price,
        year: d.getFullYear(),
        isSelf:
          !s.registration ||
          (s.registration || '').replace(/\s+/g, '').toUpperCase() === compact,
      });
    }
    return out;
  }

  // ---- Chart B: cohort verified-sales scatter -----------------------------
  private buildCohort(): void {
    this.cohort = null;
    const c = this.resolveCohort();
    if (!c) return;

    const startYear = c.year_min;
    const endYear = c.year_max;
    const compact = (this.compactRegistration || '').toUpperCase();

    // ALL of this plate's own verified auction sales in window (price in £),
    // sorted ascending by sale year.
    const ownSales = this.ownAuctionSales(startYear, endYear);

    // X-domain end extends to the latest fractional sale year so partial-year
    // clearances in the final window year (e.g. 2026-02 ≈ 2026.08, 2026-03 ≈
    // 2026.17) are plotted instead of clipped at the integer year boundary.
    // Integer axis ticks still stop at endYear so the labels stay clean.
    let domainMax = endYear;
    for (const p of c.points) {
      if (p.y > domainMax) domainMax = p.y;
    }
    for (const o of ownSales) {
      if (o.fracYear > domainMax) domainMax = o.fracYear;
    }

    // ---- y domain: £0 up to a nice maximum above the dearest clearance ----
    let hi = 0;
    for (const p of c.points) hi = Math.max(hi, p.price);
    for (const o of ownSales) hi = Math.max(hi, o.price);
    const ticks = this.niceTicks(0, hi * 1.06, 6);
    const lo = 0;
    hi = ticks[ticks.length - 1];

    const xOf = (year: number) =>
      this.PAD_L +
      ((year - startYear) / (domainMax - startYear || 1)) *
        (this.PAD_R - this.PAD_L);
    const yOf = (v: number) =>
      this.CPAD_B - ((v - lo) / (hi - lo || 1)) * (this.CPAD_B - this.CPAD_T);

    // ---- gridlines + £ labels ----
    const gridlines = ticks.map((t) => ({
      y: +yOf(t).toFixed(1),
      label: '£' + Math.round(t / 1000) + 'k',
    }));

    // ---- x ticks (every year) ----
    const xTicks: { x: number; label: string }[] = [];
    // Long histories (1 A plates from 1989) label every 5th year only.
    const span = endYear - startYear;
    const step = span > 24 ? 5 : span > 12 ? 2 : 1;
    for (let y = startYear; y <= endYear; y++) {
      if (y !== endYear && y % step !== 0) continue;
      if (y !== endYear && endYear - y < step) continue; // keep clear of the last label
      xTicks.push({ x: +xOf(y).toFixed(1), label: y.toString() });
    }

    // ---- 2021 shared reference line ----
    // Unlabelled 2021 marker removed: it read as an event, and the index is now
    // 2015-based. Kept as null so the template's *ngIf drops it cleanly.
    const refLine: { x: number } | null = null;

    // ---- cohort annual median line (points plotted at mid-year) ----
    const meds = (c.medians || [])
      .filter((m) => m.year >= startYear && m.year <= endYear)
      .sort((a, b) => a.year - b.year);
    const medianNodes = meds.map((m) => ({
      cx: +xOf(m.year + 0.5).toFixed(1),
      cy: +yOf(m.value).toFixed(1),
    }));
    const medianPath = medianNodes
      .map((n, i) => `${i === 0 ? 'M' : 'L'} ${n.cx},${n.cy}`)
      .join(' ');

    // Label the 2021 reference-year median and the latest settled (non-partial)
    // year's median — the two ends of the divergence story in the prototype.
    const labelYears: number[] = [];
    if (meds.some((m) => m.year === 2021)) labelYears.push(2021);
    const lastSettled = endYear - 1; // treat the final window year as partial
    if (lastSettled > 2021 && meds.some((m) => m.year === lastSettled)) {
      labelYears.push(lastSettled);
    }
    const medianLabels = labelYears.map((yr) => {
      const m = meds.find((mm) => mm.year === yr)!;
      return {
        x: +xOf(yr + 0.5).toFixed(1),
        y: +(yOf(m.value) - 10).toFixed(1),
        text: this.formatK(m.value),
      };
    });

    // ---- all clearance dots (the plate's own sale is drawn gold separately) ----
    const dots: CohortDot[] = [];
    for (const p of c.points) {
      if (p.y < startYear || p.y > domainMax) continue;
      if ((p.plate || '').toUpperCase() === compact) continue; // own → gold
      const isSister = p.cls === c.sister_class;
      dots.push({
        cx: +xOf(p.y).toFixed(1),
        cy: +yOf(p.price).toFixed(1),
        isSister,
        title: `${p.plate}: £${p.price.toLocaleString('en-GB')}, ${p.ym}`,
        plate: p.plate,
        priceLabel: '£' + p.price.toLocaleString('en-GB'),
        ym: p.ym,
        clsLabel: isSister ? c.sister_label : c.primary_label,
      });
    }

    // ---- this plate's own clearances, drawn gold ----
    // Only the latest carries the visible label + axis date; all are connected
    // by a dashed gold line when there is more than one.
    let latestIdx = -1;
    let latestFrac = -Infinity;
    ownSales.forEach((o, i) => {
      if (o.fracYear >= latestFrac) {
        latestFrac = o.fracYear;
        latestIdx = i;
      }
    });
    const owns: OwnMarker[] = ownSales.map((o, i) => {
      const cx = +xOf(o.fracYear).toFixed(1);
      return {
        cx,
        cy: +yOf(o.price).toFixed(1),
        tickY: this.CPAD_B - 17,
        label: `${this.registration} · £${o.price.toLocaleString('en-GB')}`,
        when: o.when,
        isLatest: i === latestIdx,
        anchor:
          cx < this.PAD_L + 90 ? 'start' : cx > this.PAD_R - 90 ? 'end' : 'middle',
        title: `${this.registration}: £${o.price.toLocaleString('en-GB')}, ${o.when}`,
      };
    });
    const ownConnectorPath =
      owns.length >= 2
        ? owns.map((o, i) => `${i === 0 ? 'M' : 'L'} ${o.cx},${o.cy}`).join(' ')
        : null;

    const hasSister = dots.some((d) => d.isSister);

    this.cohort = {
      title: `${c.label}: verified sales`,
      metaRange: `${c.n} auction sales · ${startYear} to ${endYear}`,
      blurb:
        'Each dot is one verified auction sale of a comparable plate, price paid including fees. ' +
        (c.basis ? `Comparable here means ${c.basis}. ` : '') +
        (owns.length
          ? `${this.registration}'s own ${owns.length === 1 ? 'sale is' : 'sales are'} in gold.`
          : 'The line is the median for each year.'),
      viewBox: `0 0 ${this.W} ${this.CH}`,
      plot: {
        x: this.PAD_L,
        y: this.CPAD_T,
        w: this.PAD_R - this.PAD_L,
        h: this.CPAD_B - this.CPAD_T,
      },
      gridlines,
      xTicks,
      refLine,
      medianPath,
      medianNodes,
      medianLabels,
      dots,
      owns,
      ownConnectorPath,
      primaryLabel: this.capFirst(c.primary_label),
      sisterLabel: this.capFirst(c.sister_label),
      hasSister,
      note: c.note || this.cohortFile!.meta.note,
      source: this.cohortSource(c.note || this.cohortFile!.meta.note),
    };
  }

  // ---- Chart B interactive tooltip (tap on mobile, hover on desktop) -------
  // Builds a small tooltip box anchored to the dot, clamped inside the plot so
  // it never overflows the SVG. Coordinates are in viewBox space, so the box
  // scales with the chart.
  showCohortTip(d: CohortDot): void {
    const lines: { t: string; head?: boolean }[] = [
      { t: d.plate, head: true },
      { t: d.priceLabel },
      { t: d.ym },
      { t: d.clsLabel },
      { t: 'Verified auction sale' },
    ];
    const w = 168;
    const h = lines.length * 16 + 14;
    let x = d.cx + 14;
    let y = d.cy - h - 10;
    if (x + w > this.PAD_R) x = d.cx - w - 14; // flip to the left near the edge
    if (x < this.PAD_L) x = this.PAD_L;
    if (y < this.CPAD_T) y = d.cy + 14; // drop below the dot near the top
    this.activeTip = { x: +x.toFixed(1), y: +y.toFixed(1), w, h, lines };
  }

  hideCohortTip(): void {
    this.activeTip = null;
  }

  // Resolve this plate's verified-sales cohort (shared by Chart B + the two
  // evidence cards), or null when no cohort applies / has too little coverage.
  private resolveCohort(): CohortDef | null {
    if (this.newIssue) return null;
    const layoutKey = routeToDatelessLayout(
      this.formatKey,
      this.compactRegistration
    );
    if (layoutKey) return this.datelessLayoutCohort(layoutKey);
    if (!this.cohortFile || this.cohortLoadError) return null;
    const key = routeToCohort(this.formatKey, this.compactRegistration);
    if (!key) return null;
    const c = this.cohortFile.cohorts[key];
    if (!c || !c.points || c.points.length < 4) return null;
    return c;
  }

  // Build this plate's comparable cohort from its dateless layout file:
  //   small layouts (1 A, AB 1, A 1234): every sale in the layout;
  //   otherwise: sales sharing the letters or the number (the number only
  //   with the same first letter when it is shared by a crowd, e.g. ABC 1),
  //   widened to the same first letter when that leaves too few sales.
  // Both arrangements are kept and coloured, as on the 3-character card.
  private datelessLayoutCohort(key: string): CohortDef | null {
    const file = MarketInfoComponent.layoutCache.get(key);
    const compact = (this.compactRegistration || '').toUpperCase();
    const subject = parseDatelessParts(compact);
    if (!file || !subject) return null;
    const memoKey = key + ':' + compact;
    if (this.layoutCohortFor === memoKey) return this.layoutCohort;

    const rows: { parts: DatelessParts; r: [string, number, number] }[] = [];
    const add = (src: [string, number, number][]) => {
      for (const r of src) {
        if (r[0] === compact) continue; // own sales are drawn gold separately
        const parts = parseDatelessParts(r[0]);
        if (parts && sameNumberClass(subject, parts)) rows.push({ parts, r });
      }
    };
    add(file.rows);
    const whole = rows.length <= WHOLE_LAYOUT_MAX;
    const crowded =
      rows.filter((x) => x.parts.digits === subject.digits).length >
      CROWDED_NUMBER;
    const pick = (widen: boolean) =>
      rows.filter(
        (x) => whole || datelessMatch(subject, x.parts, crowded, widen) != null
      );
    let sel = pick(false);
    // Too few sales since 2016 (or a tiny layout such as 1 A): reach back to
    // earlier sales of the same kind (1 D sold in 2009, 1 A in 1989) before
    // widening to the first letter.
    let yearMin = file.year_min;
    if (sel.length < THIN_COHORT || file.n < TINY_LAYOUT) {
      const early = MarketInfoComponent.earlyCache.get(key);
      if (early) {
        add(early.rows);
        sel = pick(false);
        if (sel.some((x) => x.r[1] < file.year_min)) {
          yearMin = Math.floor(Math.min(...sel.map((x) => x.r[1])));
        }
      } else {
        this.loadEarly(key);
      }
    }
    let widened = false;
    if (!whole && sel.length < THIN_COHORT) {
      sel = pick(true);
      widened = true;
      yearMin = Math.min(
        yearMin,
        ...sel.map((x) => Math.floor(x.r[1]))
      );
    }

    const points: CohortSalePoint[] = sel.map(({ parts, r }) => {
      const yr = Math.floor(r[1]);
      const mo = Math.min(12, Math.round((r[1] - yr) * 12) + 1);
      return {
        plate: r[0],
        cls: parts.digitsFirst ? file.primary_class : file.sister_class,
        y: r[1],
        ym: `${yr}-${String(mo).padStart(2, '0')}`,
        price: r[2],
      };
    });
    const layoutName = datelessLayoutName(file.letters, file.digits);
    this.layoutCohortFor = memoKey;
    this.layoutCohort =
      points.length < 4
        ? null
        : {
            label: `${layoutName} dateless`,
            primary_class: file.primary_class,
            sister_class: file.sister_class,
            primary_label: file.primary_label,
            sister_label: file.sister_label,
            year_min: yearMin,
            year_max: file.year_max,
            n: points.length,
            points,
            medians: yearlyMedians(points, yearMin, file.year_max),
            basis: describeDatelessCohort(
              subject,
              whole,
              crowded,
              widened,
              layoutName
            ),
            note: file.meta.note,
          };
    return this.layoutCohort;
  }

  // All of this plate's own verified auction clearances in window (price in £),
  // sorted ascending by fractional sale year. Empty when none qualify.
  private ownAuctionSales(
    startYear: number,
    endYear: number
  ): { price: number; fracYear: number; when: string; ym: string }[] {
    if (!this.sales || this.sales.length === 0) return [];
    const out: {
      price: number;
      fracYear: number;
      when: string;
      ym: string;
    }[] = [];
    for (const s of this.sales) {
      if (!this.isAuctionSale(s.saleType)) continue;
      const d = s && s.sold_date ? new Date(s.sold_date) : null;
      if (!d || isNaN(d.getTime())) continue;
      const fracYear = d.getFullYear() + d.getMonth() / 12;
      // Compare calendar years: a sale in, say, May of the final year
      // (2026.33) is inside a window that ends in 2026.
      if (d.getFullYear() < startYear || d.getFullYear() > endYear) continue;
      const pounds = this.salePounds(s);
      if (pounds == null) continue;
      const when = d
        .toLocaleString('en-GB', { month: 'short', year: 'numeric' })
        .toUpperCase();
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0'
      )}`;
      out.push({ price: pounds, fracYear, when, ym });
    }
    out.sort((a, b) => a.fracYear - b.fracYear);
    return out;
  }

  // ---- Section 1: Comparable Market (market-POSITION chart) ----------------
  // Answers ONE question: "Where does this registration sit within today's
  // advertised market?". Every point — including the gold marker — comes from
  // the LIVE advertised market only. NEVER verified sales, sold medians or
  // index values (Mode B's signal included). Two modes:
  //   Mode A (advertised):   gold marker = this registration's ACTUAL asking
  //                          price, labelled with the registration.
  //   Mode B (not advertised): gold marker = a live-market SIGNAL derived only
  //                          from the current comparable listings (their
  //                          median), to give an approximate position when no
  //                          asking price exists. Not a valuation.
  // Each live dot supports a hover tooltip (reg / asking / dealer count) and a
  // click-through to its registration page.
  private buildComparable(): void {
    this.comparable = null;
    // Dateless plates only for now (other formats follow), never DVLA stock.
    if (this.newIssue || this.formatKey !== 'dateless') return;
    const subjectParts = parseDatelessParts(this.compactRegistration);
    if (!subjectParts) return;
    const gbp = (v: number) => '£' + Math.round(v).toLocaleString('en-GB');

    // The registration's own ADVERTISED price, if it is on the market. Prefer a
    // direct PlateX price (askingPrice); otherwise fall back to the lowest live
    // dealer-listing total (marketLow). On the market via EITHER source ⇒ plot
    // the real marker, never a signal.
    const advertised =
      this.askingPrice != null && this.askingPrice > 0
        ? this.askingPrice
        : this.marketLow != null && this.marketLow > 0
        ? this.marketLow
        : null;
    const forSale = advertised != null;

    // LIVE comparable cloud ONLY (asking prices). null = still loading.
    const stillLoading = this.compListings === null;
    const inClass = (reg: string) => {
      const o = parseDatelessParts(reg.replace(/\s+/g, '').toUpperCase());
      return !o || sameNumberClass(subjectParts, o);
    };
    let live = (this.compListings || []).filter(
      (l) => l.pricePounds > 0 && inClass(l.regRaw)
    );

    // Wider same-format NN_L evidence (grey). Exclude any reg already in the
    // close cohort (green) so nothing is plotted twice. Quartiles/median are NOT
    // recomputed from this set — it is purely additional visual density.
    const liveKeys = new Set(
      live.map((l) => l.regRaw.replace(/\s+/g, '').toUpperCase())
    );
    let wide = (this.wideListings || []).filter(
      (w) =>
        w.pricePounds > 0 &&
        inClass(w.regRaw) &&
        !liveKeys.has(w.regRaw.replace(/\s+/g, '').toUpperCase())
    );
    // A whole layout can hold a few extreme listings (one £550k ABC 12 among
    // hundreds under £20k) that would squash every other dot against the left
    // edge. Grey is only context: keep it within the green listings, the plate
    // itself and the 95th percentile of the grey prices.
    if (wide.length >= 20) {
      const wp = wide.map((w) => w.pricePounds).sort((a, b) => a - b);
      let cap = this.quantile(wp, 0.95);
      for (const l of live) cap = Math.max(cap, l.pricePounds);
      if (this.askingPrice && this.askingPrice > 0) cap = Math.max(cap, this.askingPrice);
      if (this.marketLow && this.marketLow > 0) cap = Math.max(cap, this.marketLow);
      wide = wide.filter((w) => w.pricePounds <= cap);
    }
    // A listing priced at ten times the market or more (F 1 at £15m among
    // 1 A plates around £500k) is not evidence for another plate: drop it from
    // this card entirely, figures included. Its own page still shows it.
    {
      const all = live.concat(wide).map((l) => l.pricePounds).sort((a, b) => a - b);
      if (all.length >= 5) {
        const ceiling = 10 * this.quantile(all, 0.5);
        live = live.filter((l) => l.pricePounds <= ceiling);
        wide = wide.filter((l) => l.pricePounds <= ceiling);
      }
    }
    // Small layouts (1 A, AB 1 …): when almost nothing shares the letters or
    // the number, the whole layout is the comparable set, as on the sales card.
    // A big layout (1,700 AB 1234 listings) stays grey context: promoting it
    // would paint a wall of green that compares with nothing in particular.
    // There the cohort narrows to the same first letter instead, as on the
    // sales card.
    const wholeLayout =
      live.length < 5 && wide.length >= 5 && wide.length <= WHOLE_LAYOUT_MAX;
    let askWidened = false;
    if (wholeLayout) {
      live = live.concat(wide);
      wide = [];
    } else if (live.length < 5 && wide.length > WHOLE_LAYOUT_MAX) {
      const initial = subjectParts.letters[0];
      const same = wide.filter((w) => {
        const o = parseDatelessParts(w.regRaw.replace(/\s+/g, '').toUpperCase());
        return !!o && o.letters[0] === initial;
      });
      if (live.length + same.length >= 5) {
        const sameKeys = new Set(same.map((w) => w.regRaw));
        live = live.concat(same);
        wide = wide.filter((w) => !sameKeys.has(w.regRaw));
        askWidened = true;
      }
    }

    // Live-market quartiles, computed from the asking prices of the live cloud
    // itself — the sole basis for the IQR band, the median line, AND (in Mode B)
    // the market-signal position. No sold/cohort data is used here.
    const livePrices = live.map((l) => l.pricePounds).sort((a, b) => a - b);
    const liveQ1 = livePrices.length ? this.quantile(livePrices, 0.25) : null;
    const liveMed = livePrices.length ? this.quantile(livePrices, 0.5) : null;
    const liveQ3 = livePrices.length ? this.quantile(livePrices, 0.75) : null;

    const baseVm = {
      heading: 'Comparable asking prices',
      subtitle: `Where does ${this.registration} sit within today's advertised market?`,
      tag: 'Live listings',
      sourceLabel: 'Comparable plates for sale',
      liveLabel: 'Asking prices today',
      liveLo: livePrices.length ? gbp(livePrices[0]) : null,
      liveMedian: liveMed != null ? gbp(liveMed) : null,
      liveHi: livePrices.length ? gbp(livePrices[livePrices.length - 1]) : null,
      framing: forSale
        ? 'Each dot is a comparable registration currently advertised for sale, at its actual asking price. The gold marker is this registration at its own asking price. Hover any dot for its asking price and dealer count, or click through to its page.'
        : 'Each dot is a comparable registration currently advertised for sale, at its actual asking price. This registration is not currently advertised, so the gold marker is an approximate position derived only from the current live listings. It is a live-market signal, not a valuation.',
      disclaimer:
        'Positioning reflects the live advertised market only. It is not a formal valuation of this registration.',
      source: 'Source: live dealer listings on PlateX today. Asking prices, not sale prices.',
    };

    // No live comparables (still loading, or none advertised) → quiet note. We
    // never fall back to verified sales / sold medians here.
    if (live.length === 0) {
      this.comparable = {
        ...baseVm,
        countLabel: stillLoading
          ? 'Checking the live market…'
          : 'None currently advertised',
        hasChart: false,
        viewBox: `0 0 ${this.W} 1`,
        plot: { x: this.PAD_L, y: 0, w: this.PAD_R - this.PAD_L, h: 0 },
        xTicks: [],
        iqrBand: null,
        medianX: null,
        points: [],
        widePoints: [],
        wideLabel: null,
        subject: null,
        signal: null,
        emptyNote: stillLoading
          ? 'Loading comparable registrations currently advertised for sale…'
          : forSale
          ? 'No other comparable registrations from this cohort are currently advertised. This listing stands alone in the live market right now.'
          : 'No comparable registrations from this cohort are currently advertised for sale, so a live-market position cannot be shown right now.',
        liveBasis:
          forSale && advertised != null
            ? `This registration is advertised at ${gbp(advertised)}.`
            : 'A live-market position will appear here once comparable registrations are advertised for sale.',
      };
      return;
    }

    // Mode B signal price = live-market median (current listings only).
    const signalPrice = liveMed != null ? liveMed : livePrices[0];
    // The gold-marker anchor price (for axis-domain inclusion).
    const anchor = forSale ? (advertised as number) : signalPrice;

    // A real but extreme listing (F 1 asking £15m among 1 A plates under
    // £1.3m) would flatten every other dot. Listings above 3x the 95th
    // percentile stay in the headline figures but are not plotted; the count
    // line says how many sit above the scale.
    // The plate's own asking price can be the extreme one (F 1 at £15m): it is
    // then pinned to the right edge and its label still gives the real price.
    const plotted = live.concat(wide).map((l) => l.pricePounds).sort((a, b) => a - b);
    const plotCap =
      plotted.length >= 10
        ? 3 * this.quantile(plotted, 0.95)
        : Infinity;
    const anchorOff = anchor > plotCap;
    const offScale =
      live.filter((l) => l.pricePounds > plotCap).length +
      wide.filter((l) => l.pricePounds > plotCap).length;
    const livePlot = live.filter((l) => l.pricePounds <= plotCap);
    const widePlot = wide.filter((l) => l.pricePounds <= plotCap);

    // ---- x (price) domain across the live cloud + anchor, nice ticks ----
    let hi = 0;
    let lo = Infinity;
    for (const l of livePlot) {
      hi = Math.max(hi, l.pricePounds);
      lo = Math.min(lo, l.pricePounds);
    }
    // Widen the domain to the grey same-format dots so they all stay on-axis.
    for (const w of widePlot) {
      hi = Math.max(hi, w.pricePounds);
      lo = Math.min(lo, w.pricePounds);
    }
    if (!anchorOff) {
      hi = Math.max(hi, anchor);
      lo = Math.min(lo, anchor);
    }
    lo = Math.max(0, lo - (hi - lo) * 0.08);
    const ticks = this.niceTicks(lo, hi * 1.04, 5);
    lo = ticks[0];
    hi = ticks[ticks.length - 1];

    const xOf = (v: number) =>
      this.PAD_L + ((v - lo) / (hi - lo || 1)) * (this.PAD_R - this.PAD_L);

    const xTicks = ticks.map((t) => ({
      x: +xOf(t).toFixed(1),
      label: '£' + Math.round(t / 1000) + 'k',
    }));

    // ---- IQR band + median, from the LIVE cloud (subtle context) ----
    const iqrBand =
      liveQ1 != null && liveQ3 != null
        ? {
            x: +xOf(liveQ1).toFixed(1),
            w: +(xOf(liveQ3) - xOf(liveQ1)).toFixed(1),
          }
        : null;
    const medianX = liveMed != null ? +xOf(liveMed).toFixed(1) : null;

    // ---- live comparable dots, deterministic jitter to avoid stacking ----
    const cy0 = (this.CM_T + this.CM_B) / 2;
    const amp = (this.CM_B - this.CM_T) / 2 - 6;
    const points: CompPoint[] = livePlot.map((l) => ({
      cx: +xOf(l.pricePounds).toFixed(1),
      cy: +(cy0 + this.jitter(l.regRaw) * amp).toFixed(1),
      regRaw: l.regRaw,
      regFmt: l.regFmt,
      priceLabel: gbp(l.pricePounds),
    }));

    // ---- wider same-format NN_L dots (grey, behind the green close cohort) ----
    const widePoints: CompPoint[] = widePlot.map((l) => ({
      cx: +xOf(l.pricePounds).toFixed(1),
      cy: +(cy0 + this.jitter(l.regRaw) * amp).toFixed(1),
      regRaw: l.regRaw,
      regFmt: l.regFmt,
      priceLabel: gbp(l.pricePounds),
    }));

    // ---- subject (advertised) OR live-market signal (not advertised) --------
    let subject: ComparableMarketVM['subject'] = null;
    let signal: ComparableMarketVM['signal'] = null;
    if (forSale) {
      subject = {
        cx: anchorOff ? this.PAD_R : +xOf(advertised as number).toFixed(1),
        cy: +cy0.toFixed(1),
        axisY: this.CM_B,
        label: this.registration,
        sub:
          `This registration · asking ${gbp(advertised as number)}` +
          (anchorOff ? ' (above the scale)' : ''),
      };
    } else {
      signal = {
        cx: +xOf(signalPrice).toFixed(1),
        cy: +cy0.toFixed(1),
        axisY: this.CM_B,
        label: this.registration,
        sub: `Live-market signal · ~${gbp(signalPrice)} (based on current listings)`,
      };
    }

    this.comparable = {
      ...baseVm,
      // Counts OTHER comparable plates, not this plate's sellers — say so, or it
      // reads as contradicting "Compare all N sellers" above.
      countLabel:
        `${live.length} comparable plate${live.length === 1 ? '' : 's'} advertised` +
        (offScale ? ` · ${offScale} above the scale` : ''),
      hasChart: true,
      viewBox: `0 0 ${this.W} ${this.CMH}`,
      plot: {
        x: this.PAD_L,
        y: this.CM_T,
        w: this.PAD_R - this.PAD_L,
        h: this.CM_B - this.CM_T,
      },
      xTicks,
      iqrBand,
      // When this registration is itself for sale (gold subject marker), the
      // live-market median line competes with the asking-price marker and reads
      // as confusing — drop it. The IQR band stays for quiet context. The median
      // is retained only in signal mode (not advertised), where it anchors the
      // market-signal marker.
      medianX: forSale ? null : medianX,
      points,
      widePoints,
      wideLabel: widePoints.length
        ? 'Other plates in the same format'
        : null,
      subject,
      signal,
      emptyNote: null,
      liveBasis: `Based on ${live.length} comparable registration${
        live.length === 1 ? '' : 's'
      } currently advertised: ${describeDatelessCohort(
        subjectParts,
        wholeLayout,
        this.askCrowded,
        askWidened,
        datelessLayoutName(
          subjectParts.letters.length,
          subjectParts.digits.length
        )
      )}.`,
    };
  }

  // ---- Chart 1 interactive tooltip + dealer count + click-through ----------
  // Build a tooltip box on hover (reg / asking / dealer count), lazily resolve
  // the dealer count via the EXISTING payment-details endpoint, and patch the
  // tip in place when it arrives (only if the user is still on the same dot).
  showCompTip(p: CompPoint): void {
    this.activeCompReg = p.regRaw;
    const dc = MarketInfoComponent.dealerCounts.get(p.regRaw);
    this.activeCompTip = this.placeCompTip(p, dc);
    if (dc == null) this.fetchDealerCount(p);
  }

  hideCompTip(): void {
    this.activeCompTip = null;
    this.activeCompReg = '';
  }

  // Click-through to the comparable registration's own detail page (same nav as
  // the "Similar registrations" cards).
  openComp(p: CompPoint): void {
    const reg = (p.regRaw || '').replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate(['/plate-detail-v2', reg]);
  }

  private placeCompTip(p: CompPoint, dealerCount: number | undefined): CompTip {
    const lines: { t: string; head?: boolean }[] = [
      { t: p.regFmt, head: true },
      { t: 'Asking ' + p.priceLabel },
      { t: dealerCount == null ? 'Dealers: checking…' : `Dealers: ${dealerCount}` },
      { t: 'Click to view registration' },
    ];
    const w = 184;
    const h = lines.length * 16 + 14;
    let x = p.cx + 14;
    let y = p.cy - h - 10;
    if (x + w > this.PAD_R) x = p.cx - w - 14; // flip near the right edge
    if (x < this.PAD_L) x = this.PAD_L;
    if (y < this.CM_T) y = p.cy + 14; // drop below near the top
    return { x: +x.toFixed(1), y: +y.toFixed(1), w, h, lines };
  }

  private fetchDealerCount(p: CompPoint): void {
    const reg = (p.regRaw || '').replace(/\s+/g, '');
    if (!reg) return;
    this.registrationService.getPlatePaymentDetails(reg).subscribe(
      (details: PlatePaymentDetails) => {
        const sellers =
          (details && details.sellersResp && details.sellersResp.sellers) || [];
        const count = sellers.filter((x) => x && x.totalPrice > 0).length;
        MarketInfoComponent.dealerCounts.set(p.regRaw, count);
        // Patch the tip only if the user is still hovering this same dot.
        if (this.activeCompReg === p.regRaw && this.activeCompTip) {
          this.activeCompTip = this.placeCompTip(p, count);
        }
      },
      () => {
        MarketInfoComponent.dealerCounts.set(p.regRaw, 0);
      }
    );
  }

  // Linear-interpolated quantile over an ascending-sorted numeric array.
  private quantile(sortedAsc: number[], q: number): number {
    if (sortedAsc.length === 0) return 0;
    if (sortedAsc.length === 1) return sortedAsc[0];
    const pos = (sortedAsc.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    return base + 1 < sortedAsc.length
      ? sortedAsc[base] + rest * (sortedAsc[base + 1] - sortedAsc[base])
      : sortedAsc[base];
  }

  // Deterministic [-1, 1] jitter from a registration string, so the strip is
  // stable across renders (no random reshuffle on rebuild).
  private jitter(seed: string): number {
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
      h = (h * 31 + seed.charCodeAt(i)) & 0xffff;
    }
    return (h / 0xffff) * 2 - 1;
  }

  // ---- Section 2: Previous Sale in Context (condensed to four facts) -------
  // Last verified sale price, sale date, cohort median at the time, relative
  // position vs cohort. Evidence-framed — never scaled into a current worth.
  private buildPrevSale(): void {
    this.prevSale = null;
    const c = this.resolveCohort();
    if (!c) return;
    const ownSales = this.ownAuctionSales(c.year_min, c.year_max);
    if (ownSales.length === 0) return;
    const own = ownSales[ownSales.length - 1]; // latest own sale
    const saleYear = Math.floor(own.fracYear);
    const med = (c.medians || []).find((m) => m.year === saleYear);
    const price = '£' + own.price.toLocaleString('en-GB');
    const date = this.titleCaseWhen(own.when);

    if (!med || med.value <= 0) {
      this.prevSale = {
        price,
        date,
        cohortMedian: null,
        deltaLabel: 'No same-year cohort median',
        deltaDir: 'na',
      };
      return;
    }
    const cohortMedian = '£' + Math.round(med.value).toLocaleString('en-GB');
    const pct = Math.round((own.price / med.value - 1) * 100);
    let deltaLabel: string;
    let deltaDir: 'below' | 'above' | 'inline';
    if (pct <= -3) {
      deltaLabel = `${Math.abs(pct)}% below cohort median`;
      deltaDir = 'below';
    } else if (pct >= 3) {
      deltaLabel = `${pct}% above cohort median`;
      deltaDir = 'above';
    } else {
      deltaLabel = 'In line with cohort median';
      deltaDir = 'inline';
    }
    this.prevSale = { price, date, cohortMedian, deltaLabel, deltaDir };
  }

  // "JUN 2022" → "June 2022" for prose copy.
  private titleCaseWhen(when: string): string {
    const months: { [k: string]: string } = {
      JAN: 'January',
      FEB: 'February',
      MAR: 'March',
      APR: 'April',
      MAY: 'May',
      JUN: 'June',
      JUL: 'July',
      AUG: 'August',
      SEP: 'September',
      OCT: 'October',
      NOV: 'November',
      DEC: 'December',
    };
    const m = /^([A-Z]{3})\s+(\d{4})$/.exec(when);
    if (!m) return when;
    return `${months[m[1]] || m[1]} ${m[2]}`;
  }

  // Numeric pound value of a sale (SoldPlate prices are minor units / pence).
  private salePounds(s: SoldPlate): number | null {
    let minor: number | null = null;
    if (s.sold_price != null && String(s.sold_price).trim() !== '') {
      const n = parseFloat(String(s.sold_price).replace(/[^0-9.]/g, ''));
      if (!isNaN(n) && n > 0) minor = n;
    }
    if (minor == null && s.price != null && s.price > 0) minor = s.price;
    if (minor == null) return null;
    return Math.round(minor / 100);
  }

  // Compact £ in thousands, e.g. 49098 → "£49.1k", 66000 → "£66k".
  private formatK(v: number): string {
    const k = v / 1000;
    const s =
      k >= 100
        ? Math.round(k).toString()
        : (Math.round(k * 10) / 10).toString().replace(/\.0$/, '');
    return '£' + s + 'k';
  }

  private isAuctionSale(t: SaleType): boolean {
    return (
      t === SaleType.DVLA_AUTION ||
      t === SaleType.DVLA_AUCTION_IMPORT ||
      t === SaleType.THIRD_PARTY_AUCTION_IMPORT ||
      t === SaleType.VERIFIED_AUCTION
    );
  }

  // Price is stored in minor units (the sold-data-table divides sold_price by
  // 100). Prefer sold_price; fall back to the numeric price field.
  private formatPrice(s: SoldPlate): string {
    let minor: number | null = null;
    if (s.sold_price != null && String(s.sold_price).trim() !== '') {
      const n = parseFloat(String(s.sold_price).replace(/[^0-9.]/g, ''));
      if (!isNaN(n) && n > 0) minor = n;
    }
    if (minor == null && s.price != null && s.price > 0) minor = s.price;
    if (minor == null) return '';
    return '£' + Math.round(minor / 100).toLocaleString('en-GB');
  }

  // Pleasant axis ticks across [lo,hi] with ~count divisions.
  private niceTicks(lo: number, hi: number, count: number): number[] {
    const range = hi - lo || 1;
    const rawStep = range / count;
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const norm = rawStep / mag;
    let step: number;
    if (norm < 1.5) step = 1;
    else if (norm < 3) step = 2;
    else if (norm < 7) step = 5;
    else step = 10;
    step *= mag;
    const start = Math.floor(lo / step) * step;
    const end = Math.ceil(hi / step) * step;
    const out: number[] = [];
    for (let v = start; v <= end + step * 0.5; v += step) {
      out.push(+v.toFixed(4));
    }
    return out;
  }
}
